import { Link } from 'react-router-dom'
import { Phone, Mail, MapPin } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import TransparentLogo from './TransparentLogo'


function FacebookIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
  )
}

function YoutubeIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
      <path d="M23.495 6.205a3.007 3.007 0 0 0-2.088-2.088c-1.87-.501-9.396-.501-9.396-.501s-7.507-.01-9.396.501A3.007 3.007 0 0 0 .527 6.205a31.247 31.247 0 0 0-.522 5.805 31.247 31.247 0 0 0 .522 5.783 3.007 3.007 0 0 0 2.088 2.088c1.868.502 9.396.502 9.396.502s7.506 0 9.396-.502a3.007 3.007 0 0 0 2.088-2.088 31.247 31.247 0 0 0 .5-5.783 31.247 31.247 0 0 0-.5-5.805zM9.609 15.601V8.408l6.264 3.602z"/>
    </svg>
  )
}

function InstagramIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z"/>
    </svg>
  )
}

const quickLinks = [
  { label: 'Home', to: '/' },
  { label: 'History', to: '/history' },
  { label: 'Festivals', to: '/festivals' },
  { label: 'Book Pooja', to: '/pooja' },
  { label: 'Donate', to: '/donations' },
  { label: 'Gallery', to: '/gallery' },
  { label: 'Location', to: '/location' },
]

const poojaTimings = [
  { name: 'Morning Pooja', time: '6:00 AM' },
  { name: 'Abhishekam', time: '8:00 AM' },
  { name: 'Noon Pooja', time: '12:00 PM' },
  { name: 'Evening Pooja', time: '6:00 PM' },
  { name: 'Night Pooja', time: '8:00 PM' },
]

export default function Footer() {
  const { t } = useTranslation()

  return (
    <footer className="bg-charcoal text-white">
      <div className="max-w-7xl mx-auto px-4 py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
        {/* Col 1 - Logo + about */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <TransparentLogo size={40} glow />
            <span className="font-tamil font-bold text-base leading-tight">
              ஸ்ரீ லட்சுமி நரசிம்ஹ<br />
              <span className="text-gold text-sm font-normal">பெருமாள் ஆலயம்</span>
            </span>
          </div>
          <p className="text-white/60 text-sm leading-relaxed mb-4">
            Sri Lakshmi Narasimha Perumal Temple, Ganganallur — a sacred abode of Lord Narasimha, serving devotees with devotion and grace.
          </p>
          <p className="font-tamil text-gold text-base">{t('footer.quote')}</p>
        </div>

        {/* Col 2 - Quick Links */}
        <div>
          <h4 className="font-display font-bold text-gold mb-4 text-lg">Quick Links</h4>
          <ul className="space-y-2">
            {quickLinks.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  className="text-white/60 hover:text-saffron text-sm transition-colors"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Col 3 - Pooja Timings */}
        <div>
          <h4 className="font-display font-bold text-gold mb-4 text-lg">Pooja Timings</h4>
          <ul className="space-y-2">
            {poojaTimings.map((p) => (
              <li key={p.name} className="flex justify-between text-sm">
                <span className="text-white/60">{p.name}</span>
                <span className="text-white font-medium">{p.time}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Col 4 - Contact */}
        <div>
          <h4 className="font-display font-bold text-gold mb-4 text-lg">{t('footer.contact')}</h4>
          <ul className="space-y-3">
            <li className="flex items-start gap-2 text-sm text-white/60">
              <MapPin size={16} className="text-saffron mt-0.5 shrink-0" />
              <span className="font-tamil">கெங்கநல்லூர், தமிழ்நாடு</span>
            </li>
            <li className="flex items-center gap-2 text-sm text-white/60">
              <Phone size={16} className="text-saffron shrink-0" />
              +91 9786714972
            </li>
            <li className="flex items-center gap-2 text-sm text-white/60">
              <Mail size={16} className="text-saffron shrink-0" />
              info@narasimhaperumal.org
            </li>
          </ul>
          <div className="flex gap-3 mt-5">
            <a href="#" className="text-white/50 hover:text-saffron transition-colors">
              <FacebookIcon />
            </a>
            <a href="#" className="text-white/50 hover:text-saffron transition-colors">
              <YoutubeIcon />
            </a>
            <a href="#" className="text-white/50 hover:text-saffron transition-colors">
              <InstagramIcon />
            </a>
          </div>
        </div>
      </div>

      <div className="border-t border-gold/20" />
      <div className="max-w-7xl mx-auto px-4 py-5 flex flex-col sm:flex-row items-center justify-between gap-2">
        <p className="text-white/40 text-xs">{t('footer.copyright')}</p>
        <p className="text-white/40 text-xs">Made with devotion 🙏</p>
      </div>
    </footer>
  )
}
